import HttpClientManager from '@/business/core/clients/http/helpers/manager.helper';
import { ITEM_SIZE } from '@/presentation';
import type {
	IRequestValidateBusiness,
	IRegisterBusinessRequest,
	IRegisterBusinessResponse,
	IGetBusinessInformationResponse,
	ISendSmsRequest,
	IParamsSearchOnBusiness,
	IParamsGetUsers,
	IInquiryBusinessResponse,
	IAssignBusinessCategoriesRequest,
	IVerifyBusiness,
	ICreateCustomerRequest,
	ICustomerClubDashboardParams,
	IUpdateBusinessProfileRequest,
	IUpdateCancellationPolicyRequest,
	IParamsGetBusinessesByCity,
	IRecordBusinessProfileContactRequest,
} from '@/business/modules/business/business.model';

class BusinessRepository {
	httpClient: any;
	static #instance: BusinessRepository;
	readonly BUSINESS: string = 'Businesses';

	private constructor() {
		this.httpClient = HttpClientManager.getInstance();
	}

	public static getInstance(): BusinessRepository {
		if (!BusinessRepository.#instance) {
			BusinessRepository.#instance = new BusinessRepository();
		}

		return BusinessRepository.#instance;
	}

	inquiryBusiness(): Promise<IInquiryBusinessResponse> {
		return this.httpClient(`${this.BUSINESS}`, {
			method: 'get',
		});
	}

	singupAsBusiness(data: IRegisterBusinessRequest): Promise<IRegisterBusinessResponse> {
		return this.httpClient(`${this.BUSINESS}`, {
			method: 'post',
			data: data,
			isShowNotify: true,
		});
	}

	getBusinessInformation(shopId: string): Promise<IGetBusinessInformationResponse> {
		return this.httpClient(`${this.BUSINESS}/${shopId}`, {
			method: 'get',
		});
	}

	getBusinessProfileBySlug(citySlug: string, businessSlug: string): Promise<IGetBusinessInformationResponse> {
		return this.httpClient(`${this.BUSINESS}/Profile/${citySlug}/${businessSlug}`, {
			method: 'get',
		});
	}

	recordProfileContact({ businessId, visitorId, type }: IRecordBusinessProfileContactRequest) {
		return this.httpClient(`${this.BUSINESS}/${businessId}/ProfileContact`, {
			method: 'post',
			data: { visitorId, type },
		});
	}

	updataBusinessProfile(data: IUpdateBusinessProfileRequest) {
		return this.httpClient(`${this.BUSINESS}`, {
			method: 'put',
			data,
			isShowNotify: true,
		});
	}

	requestValidateBusiness(data: IRequestValidateBusiness) {
		return this.httpClient(`${this.BUSINESS}/Validate`, {
			data,
			method: 'put',
			isShowNotify: true,
		});
	}

	searchOnBusiness(params: IParamsSearchOnBusiness) {
		const { key, city, page, size }: IParamsSearchOnBusiness = params;

		return this.httpClient(`${this.BUSINESS}/Search/Key/${key}/City/${city}/Page/${page}/Size/${size}`, {
			method: 'get',
		});
	}

	getBusinessesByCity(params: IParamsGetBusinessesByCity) {
		const { city, page, size }: IParamsGetBusinessesByCity = params;

		return this.httpClient(`${this.BUSINESS}/City/${city}/Page/${page}/Size/${size}`, {
			method: 'get',
		});
	}

	sendSms(data: ISendSmsRequest) {
		return this.httpClient(`${this.BUSINESS}/SendSMS`, {
			data,
			method: 'post',
			isShowNotify: true,
		});
	}

	getCustomerClubDashboard(params: ICustomerClubDashboardParams = {}) {
		return this.httpClient(`${this.BUSINESS}/CustomerClub`, {
			method: 'get',
			params,
		});
	}

	submitRating(data: any) {
		return this.httpClient(`${this.BUSINESS}/Points`, {
			method: 'post',
			data,
			isShowNotify: true,
		});
	}

	makeUserVIP(userId: any) {
		return this.httpClient(`${this.BUSINESS}/UserVIP`, {
			method: 'post',
			params: { userId },
			isShowNotify: true,
		});
	}

	makeUserNormal(userId: any) {
		return this.httpClient(`${this.BUSINESS}/UserVIP`, {
			method: 'delete',
			data: userId,
			isShowNotify: true,
		});
	}

	createCustomer(data: ICreateCustomerRequest) {
		return this.httpClient(`${this.BUSINESS}/Customers`, {
			method: 'post',
			data,
			isShowNotify: true,
		});
	}

	getVIPUser(params: IParamsGetUsers) {
		const { page = 1, size = ITEM_SIZE }: IParamsGetUsers = params;
		return this.httpClient(`${this.BUSINESS}/VIPUsers/Page/${page}/Size/${size}`, {
			method: 'get',
		});
	}

	getPendingBusinesses(params: IParamsGetUsers) {
		const { page = 1, size = ITEM_SIZE }: IParamsGetUsers = params;
		return this.httpClient(`${this.BUSINESS}/WaitingValid/Page/${page}/Size/${size}`, {
			method: 'get',
		});
	}

	getNormalUser(params: IParamsGetUsers) {
		const { page, size }: IParamsGetUsers = params;
		return this.httpClient(`${this.BUSINESS}/NormalUsers/Page/${page}/Size/${size}`, {
			method: 'get',
		});
	}

	assignCategoriesToBusiness(data: IAssignBusinessCategoriesRequest) {
		return this.httpClient(`${this.BUSINESS}/Categories`, {
			method: 'post',
			data,
			isShowNotify: true,
		});
	}

	getCategoriesOfBusiness(businessId: string) {
		return this.httpClient(`${this.BUSINESS}/${businessId}/Categories`, {
			method: 'get',
		});
	}

	verifyBusiness(params: IVerifyBusiness) {
		const { isValid, businessId }: IVerifyBusiness = params;
		// Deprecated: legacy admin validation endpoint. TODO replace remaining callers with Admin/Businesses/{businessId}/Validation.
		return this.httpClient(`${this.BUSINESS}/${businessId}/isValid/${isValid}`, {
			method: 'patch',
			isShowNotify: true,
		});
	}

	updateReserveTimePayment(enabled: boolean) {
		return this.httpClient(`${this.BUSINESS}/ReserveTimePayment`, {
			method: 'patch',
			params: { enabled },
			isShowNotify: true,
		});
	}

	updateOnlineReservation(enabled: boolean) {
		return this.httpClient(`${this.BUSINESS}/OnlineReservation`, {
			method: 'patch',
			params: { enabled },
			isShowNotify: true,
		});
	}

	updateCancellationPolicy(data: IUpdateCancellationPolicyRequest) {
		return this.httpClient(`${this.BUSINESS}/CancellationPolicy`, {
			method: 'patch',
			data,
			isShowNotify: true,
		});
	}

	createSlotTime(data) {
		return this.httpClient(`${this.BUSINESS}/ReserveTimeSlots`, {
			method: 'post',
			data,
			isShowNotify: true,
		});
	}
}

export default BusinessRepository;
